import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import AppleSignInButton from '../AppleSignInButton';
import { AppleSignInCancelled } from '../../services/appleSignIn';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToSignup?: () => void;
  onSwitchToForgotPassword?: () => void;
  /**
   * Op het web valt dit venster te sluiten: je komt dan op de landingspagina, waar van
   * alles te zien is. In de native app is er niets achter dit venster - sluiten bracht
   * je naar '/', en dat stuurt in de app meteen door naar /coach, waarna hetzelfde
   * venster opnieuw opent. Daar is het inlogscherm gewoon het beginscherm.
   */
  dismissible?: boolean;
}

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSwitchToSignup,
  onSwitchToForgotPassword,
  dismissible = true
}) => {
  const { t } = useTranslation('auth');
  const { login, loginWithApple: appleLogin, appleError, clearAppleError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appleLoading, setAppleLoading] = useState(false);

  /**
   * Op het web navigeert dit de pagina weg naar Apple; de belofte keert dan niet terug met
   * `true` en dat is geen fout. Een annulering evenmin: dan sluit de gebruiker gewoon de
   * dialoog en blijft het venster staan.
   */
  const handleAppleSignIn = async () => {
    setError('');
    setAppleLoading(true);
    try {
      const ok = await appleLogin();
      if (ok) {
        onClose();
      }
    } catch (err) {
      if (err instanceof AppleSignInCancelled) {
        return;
      }
      console.error('Inloggen met Apple mislukt:', err);
      setError(t('apple.failed'));
    } finally {
      setAppleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const success = await login(email, password);
      if (success) {
        // Don't call onClose() - let ProtectedRoute detect auth change
        // Just clear the form
        setEmail('');
        setPassword('');
        // Modal will close automatically via ProtectedRoute's useEffect
      } else {
        setError(t('login.invalidCredentials'));
      }
    } catch (err) {
      setError(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setPassword('');
      setError('');
      onClose();
    }
  };

  // De webflow keert terug op de pagina, niet in dit venster: als hij met een fout
  // terugkwam, tonen we die zodra het inlogvenster opengaat.
  useEffect(() => {
    if (isOpen && appleError) {
      setError(t('apple.failed'));
      clearAppleError();
    }
  }, [isOpen, appleError, clearAppleError, t]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-sluier bg-opacity-75 transition-opacity"
          onClick={handleClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block w-full align-bottom bg-white dark:ring-1 dark:ring-white/15 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="w-full">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10">
                          <img 
                            src="/apple-touch-icon-login.png" 
                            alt="Coach Login" 
                            className="h-10 w-10 rounded-full"
                          />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-hockey-brown">
                          {t('login.title')}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {t('login.subtitle')}
                        </p>
                      </div>
                    </div>
                    {dismissible && (
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    )}
                  </div>

                  {/* Error message */}
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  {/* Form fields */}
                  <div className="space-y-4">
                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('fields.email')}
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-hockey-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder={t('fields.emailPlaceholder')}
                      />
                    </div>

                    {/* Password */}
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('fields.password')}
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          disabled={loading}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-hockey-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder={t('fields.passwordPlaceholder')}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={loading}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-gray-100 px-4 py-3 sm:px-6">
              <div className="flex flex-col space-y-3">
                <button
                  type="submit"
                  disabled={loading || !email || !password}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-hockey-blue text-base font-medium text-white hover:bg-hockey-blue-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hockey-blue disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('login.submitting')}
                    </div>
                  ) : (
                    t('login.submit')
                  )}
                </button>
                
                <div className="flex justify-between items-center">

                  {onSwitchToForgotPassword && (
                    <button
                      type="button"
                      onClick={onSwitchToForgotPassword}
                      disabled={loading}
                      className="text-sm text-hockey-blue hover:text-hockey-brown disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('login.forgotPassword')}
                    </button>
                  )}
                </div>

                <div className="flex items-center">
                  <div className="flex-grow border-t border-gray-200" />
                  <span className="mx-3 text-xs uppercase tracking-wide text-gray-400">{t('apple.or')}</span>
                  <div className="flex-grow border-t border-gray-200" />
                </div>

                <AppleSignInButton onClick={handleAppleSignIn} disabled={loading} loading={appleLoading} />

                {/* De tegenhanger van signup.haveAccount in het registratievenster. Stond
                    hier nooit: op het web loopt iedereen via de landingspagina binnen, die
                    een aanmeldknop heeft. In de app bestaat die pagina niet, en zonder deze
                    link kan een nieuwe gebruiker zich nergens registreren. */}
                {onSwitchToSignup && (
                  <div className="pt-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={onSwitchToSignup}
                      disabled={loading}
                      className="text-sm text-hockey-blue hover:text-hockey-brown disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('login.noAccount')}
                    </button>
                  </div>
                )}
                
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};